import { Progress } from "antd";
import { useTranslation } from "react-i18next";
import Chip from "@/components/ui/chip";
import {
  EnrollmentStatus,
  type EducationDetailDto,
  type EnrollmentDto,
} from "../../types/client-education-detail.types";
import type { CtaDescriptor } from "../../utils/resolve-cta";
import { MilestoneTimeline } from "./MilestoneTimeline";
import styles from "./DetailSidebar.module.scss";

interface Props {
  education: EducationDetailDto;
  enrollment: EnrollmentDto;
  cta: CtaDescriptor;
  onCtaActivate: (action: CtaDescriptor["action"]) => void;
  onJumpSection: (sectionId: string) => void;
}

export function DetailSidebar(props: Props) {
  const { t } = useTranslation();
  const {
    education,
    enrollment,
    onJumpSection,
  } = props;

  const progress = Math.round(enrollment.progressPercent ?? 0);
  const instructors = education.instructors ?? [];

  return (
    <div className={styles.sidebar}>
      <h3 className={styles.title}>{education.name}</h3>

      <section className={styles.block}>
        <div className={styles.progressHeader}>
          <span>{t("clientEducations.sort.progress")}</span>
          <strong>{progress}%</strong>
        </div>
        <Progress percent={progress} showInfo={false} strokeLinecap="round" />
        {enrollment.status === EnrollmentStatus.NOT_COMPLETED && (
          <div style={{ marginTop: 8 }}>
            <Chip type="error" variant="filled" size="small">
              {t("clientEducations.status.notCompleted")}
            </Chip>
          </div>
        )}
      </section>

      <section className={styles.block}>
        <MilestoneTimeline
          education={education}
          enrollment={enrollment}
          onJump={onJumpSection}
        />
      </section>

      {instructors.length > 0 && (
        <section className={styles.block}>
          <h4 className={styles.subhead}>{t("clientDetailSidebar.instructors")}</h4>
          <ul className={styles.instructors}>
            {instructors.map((i) => (
              <li key={i.id ?? `${i.firstName}-${i.lastName}`}>
                {i.name ?? `${i.firstName ?? ""} ${i.lastName ?? ""}`.trim() ?? "-"}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
